<script setup>
import { useI18n } from 'vue-i18n';
import { formatDistanceToNow, parseISO } from 'date-fns';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
  showStatus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['revoke']);
const { t } = useI18n();

const relativeTime = dateStr => {
  if (!dateStr) return '';
  return formatDistanceToNow(parseISO(dateStr), { addSuffix: true });
};

const isUnknown = value =>
  !value || value === 'Unknown' || value === 'Unknown Browser';

const deviceIcon = () => {
  const name = (props.session.device_name || '').toLowerCase();
  if (
    name.includes('iphone') ||
    name.includes('android') ||
    name.includes('mobile')
  ) {
    return 'i-lucide-smartphone';
  }
  if (name.includes('ipad') || name.includes('tablet')) {
    return 'i-lucide-tablet';
  }
  return 'i-lucide-monitor';
};

const sessionLabel = () => {
  const parts = [];
  if (!isUnknown(props.session.browser_name)) {
    parts.push(
      props.session.browser_version
        ? `${props.session.browser_name} ${props.session.browser_version}`
        : props.session.browser_name
    );
  }
  if (!isUnknown(props.session.platform_name)) {
    parts.push(props.session.platform_name);
  }
  return (
    parts.join(' on ') ||
    t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.UNKNOWN_DEVICE')
  );
};

const locationLabel = () => {
  const parts = [];
  if (props.session.city) parts.push(props.session.city);
  if (props.session.country) parts.push(props.session.country);
  return parts.join(', ');
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-4 rounded-xl border border-n-slate-4 bg-n-background p-4"
  >
    <div class="flex min-w-0 items-start gap-3">
      <Icon
        :icon="deviceIcon()"
        class="mt-0.5 size-5 flex-shrink-0 text-n-slate-10"
      />
      <div class="flex min-w-0 flex-col gap-1">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-heading-3 text-n-slate-12">
            {{ sessionLabel() }}
          </span>
          <span
            v-if="session.current"
            class="rounded-full bg-n-teal-3 px-2 py-0.5 text-caption text-n-teal-11"
          >
            {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.CURRENT') }}
          </span>
          <span
            v-else-if="showStatus"
            class="rounded-full px-2 py-0.5 text-caption"
            :class="
              session.active === false
                ? 'bg-n-slate-3 text-n-slate-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            {{
              session.active === false
                ? $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.INACTIVE')
                : $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.ACTIVE')
            }}
          </span>
        </div>
        <span v-if="locationLabel()" class="text-body-b3 text-n-slate-11">
          {{ locationLabel() }}
        </span>
        <span
          v-if="session.last_activity_at"
          class="text-body-b3 text-n-slate-10"
        >
          {{ $t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.LAST_ACTIVE') }}
          {{ relativeTime(session.last_activity_at) }}
        </span>
      </div>
    </div>
    <Button
      v-if="session.active !== false && !session.current"
      type="button"
      faded
      xs
      :label="$t('PROFILE_SETTINGS.FORM.SESSIONS_SECTION.REVOKE')"
      color="ruby"
      @click="emit('revoke', session)"
    />
  </div>
</template>
